import {
  Box,
  Button,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useState } from 'react';
import { useLookup } from 'hooks';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { ProjectGroup } from 'library/types';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

export type CreateProjectFormParams = {
  groupId: number | null;
  title: string;
};

const defaultValues: CreateProjectFormParams = {
  groupId: null,
  title: '',
};

type CreateProjectDialogProps = {
  open: boolean;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const CreateProjectDialog = ({ open, handleClose: closeDialog, refresh }: CreateProjectDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { items: projectGroups, loading: loadingProjectGroups } = useLookup(apiService.getProjectGroups, {
    enabled: open,
  });

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [selectedProjectGroup, setSelectedProjectGroup] = useState<ProjectGroup | null>(null);

  // form validations & form state
  const schema = yup.object().shape({
    title: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    groupId: yup
      .number()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateProjectFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: CreateProjectFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.createProject(data);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="projects.createProjectCompleted"
            defaultMessage="Ürün oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setSelectedProjectGroup(null);
  };

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="projects.createProject" defaultMessage="Ürün Oluştur" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="projects.createProjectText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir ürün oluşturabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                size="small"
                options={projectGroups}
                loading={loadingProjectGroups}
                value={selectedProjectGroup}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: ProjectGroup | null) => {
                  setSelectedProjectGroup(newValue);

                  setValue('groupId', newValue ? newValue.id : null);
                }}
                getOptionLabel={(option: ProjectGroup) => option.title}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                    label={<FormattedMessage id="projects.groupTitle" defaultMessage="Ürün Grubu" />}
                    error={!!errors.groupId}
                    helperText={errors.groupId?.message}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProjectGroups ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="title"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    label={<FormattedMessage id="projects.title" defaultMessage="Adı" />}
                    fullWidth
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default CreateProjectDialog;
